/**
 * _pdfHelpers.ts — pure helpers (clamp / download / canvas / legal stamp)
 * Extracted from PdfEditor.tsx (decl-partition, zero behaviour change).
 */
import { PDFDocument, StandardFonts, rgb, type PDFFont, type PDFPage } from 'pdf-lib'
import { notify } from '../../_internal/stores/notificationStore'
import type { EditorItem, ImageItem, SignOutcome, SignatureCertificate } from './_pdfTypes'

export const RENDER_SCALE = 1.5

const PREVIEW_CSS_WIDTH = 794

export function signedPreviewScale(naturalWidth: number, dpr: number): number {
  const display = (PREVIEW_CSS_WIDTH / naturalWidth) * Math.min(2, dpr || 1)
  return Math.min(4, Math.max(RENDER_SCALE, display))
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v))
}

export function triggerDownload(bytes: Uint8Array, filename: string) {
  const blob = new Blob([new Uint8Array(bytes)], { type: 'application/pdf' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  setTimeout(() => URL.revokeObjectURL(url), 60_000)
}

export function deriveSavedFilename(filename: string, outcome: SignOutcome = 'signed'): string {
  const suffix = outcome === 'initialled' ? '(initialled)' : '(signed)'
  const dotIdx = filename.lastIndexOf('.')
  if (dotIdx <= 0) return `${filename} ${suffix}.pdf`
  const stem = filename.slice(0, dotIdx)
  return `${stem} ${suffix}.pdf`
}

/** Classify what the operator actually applied. ``'initialled'`` only
 *  when at least one signature-image placement exists AND every one of
 *  them is an initials placement — anything else (a full signature
 *  anywhere, an uploaded signature image, a legacy item without
 *  ``sigSize``, or text-only edits) stays ``'signed'`` so existing flows
 *  keep their behaviour. Pure: items -> outcome. */
export function deriveSignOutcome(items: readonly EditorItem[]): SignOutcome {
  const imageItems = items.filter(
    (it): it is ImageItem =>
      it.kind === 'signature-image' || it.kind === 'signature-drawn',
  )
  if (imageItems.length === 0) return 'signed'
  return imageItems.every((it) => it.sigSize === 'initials') ? 'initialled' : 'signed'
}

/** ARC-2/U12 (highlight → curated follow-up) — extract ONE rendered page
 *  as a PNG blob with its highlight rectangles burned in. Pure I→O:
 *  (rendered page canvas, highlight rects in PDF points) → PNG blob.
 *  The page canvas is rastered at RENDER_SCALE px/pt, so highlight
 *  coords scale by the same factor to land exactly where the operator
 *  drew them. Throws when the canvas can't produce a 2D context/blob. */
export async function extractHighlightedPagePng(
  page: { canvas: HTMLCanvasElement },
  highlights: ReadonlyArray<{ xPt: number; yPt: number; widthPt: number; heightPt: number }>,
): Promise<Blob> {
  const out = document.createElement('canvas')
  out.width = page.canvas.width
  out.height = page.canvas.height
  const ctx = out.getContext('2d')
  if (!ctx) throw new Error('Browser does not support 2D canvas')
  ctx.drawImage(page.canvas, 0, 0)
  ctx.fillStyle = 'rgba(255, 230, 50, 0.35)'
  ctx.strokeStyle = 'rgba(230, 180, 0, 0.9)'
  ctx.lineWidth = Math.max(1.5, RENDER_SCALE)
  for (const h of highlights) {
    const x = h.xPt * RENDER_SCALE
    const y = h.yPt * RENDER_SCALE
    const w = h.widthPt * RENDER_SCALE
    const hh = h.heightPt * RENDER_SCALE
    ctx.fillRect(x, y, w, hh)
    ctx.strokeRect(x, y, w, hh)
  }
  const blob = await new Promise<Blob | null>((resolve) =>
    out.toBlob((b) => resolve(b), 'image/png'),
  )
  if (!blob) throw new Error('Failed to export the highlighted page')
  return blob
}

/**
 * Convert an arbitrary image blob to a PNG ``Uint8Array``. Re-encodes
 * via a temporary canvas so JPEG / WEBP signatures still work with
 * ``pdf-lib.embedPng``. PNG inputs are returned unchanged to avoid
 * a needless round-trip.
 */

export async function ensurePngBytes(bytes: Uint8Array, mime: string): Promise<Uint8Array> {
  if (mime === 'image/png') return bytes
  const blob = new Blob([new Uint8Array(bytes)], { type: mime || 'application/octet-stream' })
  const bitmap = await createImageBitmap(blob)
  const canvas = document.createElement('canvas')
  canvas.width = bitmap.width
  canvas.height = bitmap.height
  const ctx = canvas.getContext('2d')!
  ctx.drawImage(bitmap, 0, 0)
  const outBlob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob((b) => resolve(b), 'image/png'),
  )
  if (!outBlob) return bytes
  return new Uint8Array(await outBlob.arrayBuffer())
}

/**
 * Crop a transparent canvas to its drawn-pixel bounding box. Keeps
 * a small 4-px margin so the signature doesn't sit flush against the
 * embed border. Returns the original canvas unchanged when nothing
 * is drawn (so an empty save just no-ops gracefully).
 */

/** Format a date as ``dd MMMM yyyy`` (e.g. "05 June 2026") for the
 *  Cmd+D date-drop shortcut. Zero-padded day, full month name, to match
 *  how operators date a signed document. */
export function formatSignatureDate(date: Date = new Date()): string {
  const day = String(date.getDate()).padStart(2, '0')
  const month = date.toLocaleString('en-GB', { month: 'long' })
  const year = date.getFullYear()
  return `${day} ${month} ${year}`
}

/** True when the active element is a text input the operator is typing
 *  into, so global editor shortcuts shouldn't hijack the keystroke. */
export function isTypingTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null
  if (!el) return false
  const tag = el.tagName
  return tag === 'INPUT' || tag === 'TEXTAREA' || el.isContentEditable === true
}

/** Knock out a light background to transparency, client-side, for the
 *  upload-tab PREVIEW (the authoritative removal is done server-side on
 *  save). Pixels with luminance >= ``threshold`` become transparent; the
 *  result is trimmed tight to the remaining ink. Mirrors the backend
 *  ``signature_bg_removal`` keyer so the preview matches the stored asset. */
export function keyOutBackground(
  source: HTMLCanvasElement,
  threshold = 240,
): HTMLCanvasElement {
  const ctx = source.getContext('2d')
  if (!ctx) return source
  const { width, height } = source
  if (!width || !height) return source
  const img = ctx.getImageData(0, 0, width, height)
  const d = img.data
  for (let i = 0; i < d.length; i += 4) {
    const lum = d[i] * 0.299 + d[i + 1] * 0.587 + d[i + 2] * 0.114
    if (lum >= threshold) d[i + 3] = 0
  }
  ctx.putImageData(img, 0, 0)
  return trimTransparentEdges(source)
}

/** Load image bytes into a canvas (decoded at natural size). */
export function bytesToCanvas(bytes: Uint8Array, type = 'image/png'): Promise<HTMLCanvasElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(new Blob([new Uint8Array(bytes)], { type }))
    const image = new Image()
    image.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width = image.naturalWidth || image.width
      canvas.height = image.naturalHeight || image.height
      const ctx = canvas.getContext('2d')
      if (!ctx) { URL.revokeObjectURL(url); reject(new Error('no 2d context')); return }
      ctx.drawImage(image, 0, 0)
      URL.revokeObjectURL(url)
      resolve(canvas)
    }
    image.onerror = () => { URL.revokeObjectURL(url); reject(new Error('image decode failed')) }
    image.src = url
  })
}

export function trimTransparentEdges(canvas: HTMLCanvasElement): HTMLCanvasElement {
  const ctx = canvas.getContext('2d')
  if (!ctx) return canvas
  const { width, height } = canvas
  const data = ctx.getImageData(0, 0, width, height).data
  let minX = width, minY = height, maxX = -1, maxY = -1
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const a = data[(y * width + x) * 4 + 3]
      if (a > 0) {
        if (x < minX) minX = x
        if (x > maxX) maxX = x
        if (y < minY) minY = y
        if (y > maxY) maxY = y
      }
    }
  }
  if (maxX < 0) return canvas
  const margin = 4
  const cropX = Math.max(0, minX - margin)
  const cropY = Math.max(0, minY - margin)
  const cropW = Math.min(width - cropX, maxX - minX + margin * 2)
  const cropH = Math.min(height - cropY, maxY - minY + margin * 2)
  const out = document.createElement('canvas')
  out.width = cropW
  out.height = cropH
  const outCtx = out.getContext('2d')
  if (!outCtx) return canvas
  outCtx.drawImage(canvas, cropX, cropY, cropW, cropH, 0, 0, cropW, cropH)
  return out
}

// ---------------------------------------------------------------------------
// Typed signature / initials
// ---------------------------------------------------------------------------

// Script-ish font stack for TYPED signatures. The glyphs are rasterised
// to a transparent PNG before embedding, so the exact installed face
// doesn't need to exist on the recipient's machine — what they see is
// the pixels we baked, not a font reference.

/** Selectable typed-signature faces. The operator picks one in the
 *  "Type" tab — previously the only option was a single cursive stack,
 *  which several found "silly"/illegible. Each glyph set is rasterised
 *  to a transparent PNG before embedding, so the recipient sees baked
 *  pixels, not a font reference (no need for the face to exist on their
 *  machine). Order = display order; ``[0]`` is the default. */
export interface SignatureFontOption {
  id: string
  label: string
  stack: string
}

export const SIGNATURE_FONT_OPTIONS: SignatureFontOption[] = [
  // Serif is the default ([0]) — a clean, legible printed name is what
  // most operators actually want; the cursive faces felt "silly" as the
  // first thing offered. The handwriting/script faces stay available
  // below for anyone who wants a scripted look.
  {
    id: 'serif',
    label: 'Serif',
    stack: 'Georgia, "Times New Roman", serif',
  },
  {
    id: 'print',
    label: 'Print',
    stack: 'Helvetica, Arial, sans-serif',
  },
  {
    id: 'hand',
    label: 'Handwriting',
    stack: '"Segoe Script", "Bradley Hand", "Snell Roundhand", cursive',
  },
  {
    id: 'elegant',
    label: 'Elegant',
    stack: '"Snell Roundhand", "Apple Chancery", "Edwardian Script ITC", cursive',
  },
  {
    id: 'script',
    label: 'Script',
    stack: '"Brush Script MT", "Lucida Handwriting", "Segoe Script", cursive',
  },
]

/** Default typed-signature face. Kept as a named export for back-compat
 *  with existing imports; new code should pass an explicit stack from
 *  ``SIGNATURE_FONT_OPTIONS``. */
export const SIGNATURE_FONT_STACK = SIGNATURE_FONT_OPTIONS[0].stack

/** Rasterise typed text in the chosen face onto a transparent canvas,
 *  trimmed tight so it embeds flush like a drawn signature. */

export function renderTypedSignatureCanvas(
  text: string,
  fontStack: string = SIGNATURE_FONT_STACK,
): HTMLCanvasElement {
  const fontPx = 80
  const probe = document.createElement('canvas').getContext('2d')!
  probe.font = `${fontPx}px ${fontStack}`
  const metrics = probe.measureText(text)
  const pad = 24
  const width = Math.max(40, Math.ceil(metrics.width) + pad * 2)
  const height = fontPx + pad * 2
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')!
  ctx.clearRect(0, 0, width, height)
  ctx.fillStyle = '#0f172a'
  ctx.textBaseline = 'middle'
  ctx.textAlign = 'center'
  ctx.font = `${fontPx}px ${fontStack}`
  ctx.fillText(text, width / 2, height / 2)
  return canvas
}

// ---------------------------------------------------------------------------
// Digital stamp (optional, legal electronic-signature certificate)
// ---------------------------------------------------------------------------

/** SHA-256 of ``bytes`` as lowercase hex via WebCrypto. */

export async function sha256Hex(bytes: Uint8Array): Promise<string> {
  const subtle = globalThis.crypto?.subtle
  if (!subtle) return 'unavailable'
  // Copy into a standalone buffer so digest never sees a detached /
  // shared ArrayBuffer (pdf-lib reuses buffers internally).
  const copy = new Uint8Array(bytes)
  const digest = await subtle.digest('SHA-256', copy)
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

export function formatStampDate(iso: string): string {
  try {
    return new Date(iso).toUTCString()
  } catch {
    return iso
  }
}

export function notifyStampApplied(cert: SignatureCertificate, outcome: SignOutcome = 'signed') {
  notify({
    title: 'Digital stamp applied',
    body: `${outcome === 'initialled' ? 'Initialled' : 'Signed'} by ${cert.signerName} · ref ${cert.signatureRef}`,
    kind: 'success',
    source: 'pdf-editor',
  })
}

/** Faint single-line "Digitally signed by … · ref …" along the foot of
 *  every page, so the stamp is visible without opening the appended
 *  certificate. */

export function stampSignedFooter(
  pages: PDFPage[],
  font: PDFFont,
  cert: SignatureCertificate,
  outcome: SignOutcome = 'signed',
) {
  const verb = outcome === 'initialled' ? 'initialled' : 'signed'
  const line = `Digitally ${verb} by ${cert.signerName} - ${formatStampDate(
    cert.signedAtIso,
  )} · ref ${cert.signatureRef}`
  for (const page of pages) {
    page.drawText(line, { x: 24, y: 12, size: 6, font, color: rgb(0.45, 0.45, 0.45) })
  }
}

/** Append a DocuSign-style "Certificate of completion" page binding the
 *  signer identity, a UTC timestamp, and the source-document SHA-256 —
 *  the human-readable audit trail for the electronic signature. */

export async function appendCertificatePage(
  pdfDoc: PDFDocument,
  font: PDFFont,
  cert: SignatureCertificate,
) {
  const first = pdfDoc.getPages()[0]
  const width = first ? first.getWidth() : 612
  const height = first ? first.getHeight() : 792
  const page = pdfDoc.addPage([width, height])
  const margin = 50
  let y = height - margin

  const heading = 'ELECTRONIC SIGNATURE CERTIFICATE'
  page.drawText(heading, { x: margin, y, size: 16, font, color: rgb(0.06, 0.09, 0.16) })
  y -= 12
  page.drawLine({
    start: { x: margin, y },
    end: { x: width - margin, y },
    thickness: 1,
    color: rgb(0.8, 0.82, 0.85),
  })
  y -= 28

  const rows: Array<[string, string]> = [
    ['Signer', cert.signerName],
    ['Email', cert.signerEmail || '—'],
    ['Signed (UTC)', formatStampDate(cert.signedAtIso)],
    ['Signature reference', cert.signatureRef],
  ]
  for (const [labelText, value] of rows) {
    page.drawText(`${labelText}:`, { x: margin, y, size: 11, font, color: rgb(0.4, 0.43, 0.48) })
    page.drawText(value, { x: margin + 150, y, size: 11, font, color: rgb(0.06, 0.09, 0.16) })
    y -= 22
  }

  y -= 6
  page.drawText('Source document SHA-256:', {
    x: margin, y, size: 11, font, color: rgb(0.4, 0.43, 0.48),
  })
  y -= 18
  // Wrap the 64-char hex digest into 32-char halves so it never spills
  // off the right margin regardless of page size.
  for (const chunk of [cert.sourceSha256.slice(0, 32), cert.sourceSha256.slice(32)]) {
    if (!chunk) continue
    page.drawText(chunk, { x: margin, y, size: 11, font, color: rgb(0.06, 0.09, 0.16) })
    y -= 16
  }

  y -= 18
  const disclaimer = [
    'This certificate records an electronic signature applied via Quotech. The',
    'SHA-256 above fixes the content of the signed source document at the time of',
    'signing; if a single byte of that document changes, the hash will no longer',
    'match and this certificate is void. This is an electronic signature with an',
    'audit trail (ESIGN / eIDAS), not a cryptographic PKI signature.',
  ]
  for (const lineText of disclaimer) {
    page.drawText(lineText, { x: margin, y, size: 9, font, color: rgb(0.4, 0.43, 0.48) })
    y -= 13
  }
}
